import React, { useContext, useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import Navbar from "../../ui/Navbar";
import InstructorDashboard from "./InstructorDashboard";
import InstructorClasses from "./InstructorClasses";
import InstructorLevelPlacementZone from "./InstructorLevelPlacementZone";
import AssessmentInsights from "./AssessmentInsights";
import CoPoDashboard from "../../ui/CoPoDashboard";
import HelpWidget from "../../ui/HelpWidget";
import DataReplicationWidget from "../../ui/DataReplicationWidget";
import { swalError } from "../../../utils/swalAlert";
import { fetchWithAuth } from "../../../utils/api";

export default function InstructorHome() {
  document.title = "GradeMe AI - AI Assistant for Teachers";
  const { isAuthenticated, primaryRole, user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [isCOPOEnabled, setIsCOPOEnabled] = useState(false);
  const [institute, setInstitute] = useState(null);

  const [isAuthorized, setIsAuthorized] = useState(
    isAuthenticated && primaryRole === "instructor"
  );

  useEffect(() => {
    setIsAuthorized(isAuthenticated && primaryRole === "instructor");
  }, [isAuthenticated, primaryRole]);

  useEffect(() => {
    const fetchInstituteDetails = async () => {
      const serverUrl = process.env.REACT_APP_SERVER_URL;
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${user.instituteId}`,
          {
            credentials: "include",
          }
        );
        if (!response) return;
        if (!response.ok) {
          throw new Error("Failed to fetch institute details");
        }
        const data = await response.json();
        setInstitute(data);
        setIsCOPOEnabled(data.isCOPOEnabled);
      } catch (error) {
        swalError(error.message || "Failed to load institute details.");
      }
    };

    if (isAuthorized && user?.instituteId) {
      fetchInstituteDetails();
    }
  }, [user?.instituteId, isAuthorized, navigate]);

  if (!isAuthenticated || primaryRole !== "instructor") {
    return <Navigate to="/login" replace />;
  }

  const pathParts = location.pathname.split("/");
  const currentPath = pathParts[pathParts.length - 1];
  let currentPage = "Dashboard";

  switch (currentPath) {
    case "":
      currentPage = "Classes";
      break;
    case "level-test-zone":
      currentPage = "Level Test Zone";
      break;
    case "assessment-insights":
      currentPage = "Assessment Insights";
      break;

    case "dashboard":
      currentPage = "Dashboard";
      break;
    case "co-po-dashboard":
      currentPage = "CO-PO Dashboard";
      break;
    default:
      currentPage = "Classes";
  }

  const isLanguageInst =
    user?.category === "LANGUAGE_LEARNING_INSTITUTE" ||
    user?.institute?.category === "LANGUAGE_LEARNING_INSTITUTE";

  const navLinks = [
    {
      id: "classes",
      text: isLanguageInst ? "Batches" : "Classes",
      to: "/instructor",
      icon: isLanguageInst ? "fa-solid fa-layer-group" : "fa-solid fa-chalkboard",
      end: true,
    },
    !isLanguageInst && { id: "assessment-insights", text: "Assessment Insights", to: "/instructor/assessment-insights", icon: "fa-solid fa-lightbulb" },
    isLanguageInst && {
      id: "level-test-zone",
      text: "Level Test Zone",
      to: "/instructor/level-test-zone",
      icon: "fa-solid fa-graduation-cap",
    },
    { id: "dashboard", text: "Dashboard", to: "/instructor/dashboard", icon: "fa-solid fa-chart-line" },
    isCOPOEnabled && { id: "co-po-dashboard", text: "CO-PO Dashboard", to: "/instructor/co-po-dashboard", icon: "fa-solid fa-graduation-cap"},
  ];

  let breadcrumbs = [];
  switch (currentPage) {
    case "Classes":
      breadcrumbs = [
        { id: "home", name: "Home", path: "/instructor" },
        {
          id: "classes",
          name: isLanguageInst ? "Batches" : "Classes",
          path: "/instructor",
          isLast: true,
        },
      ];
      break;
    case "Level Test Zone":
      breadcrumbs = [
        { id: "home", name: "Home", path: "/instructor" },
        {
          id: "level-test-zone",
          name: "Level Test Zone",
          path: "/instructor/level-test-zone",
          isLast: true,
        },
      ];
      break;
    case "Assessment Insights":
      breadcrumbs = [
        { id: "home", name: "Home", path: "/instructor" },
        {
          id: "assessment-insights",
          name: "Assessment Insights",
          path: "/instructor/assessment-insights",
          isLast: true,
        },
      ];
      break;

    case "Dashboard":
      breadcrumbs = [
        { id: "home", name: "Home", path: "/instructor" },
        {
          id: "dashboard",
          name: "Dashboard",
          path: "/instructor/dashboard",
          isLast: true,
        },
      ];
      break;
    case "CO-PO Dashboard":
      breadcrumbs = [
        { id: "home", name: "Home", path: "/instructor" },
        {
          id: "co-po-dashboard",
          name: "CO-PO Dashboard",
          path: "/instructor/co-po-dashboard",
          isLast: true,
        },
      ];
      break;
    default:
      breadcrumbs = [
        {
          id: "classes",
          name: "Classes",
          path: "/instructor",
          isLast: true,
        },
      ];
  }

  return (
    <>
      <Navbar links={navLinks.filter(Boolean)} breadcrumbs={breadcrumbs} />
      <div className="instructor-content">
        <Routes>
          <Route index element={<InstructorClasses />} />
          <Route path="dashboard" element={<InstructorDashboard />} />
          <Route path="assessment-insights" element={<AssessmentInsights />} />
          <Route path="level-test-zone" element={<InstructorLevelPlacementZone institute={institute} />} />

          {isCOPOEnabled && (
            <Route path="co-po-dashboard" element={<CoPoDashboard role="instructor" instructorId={user.id} />} />
          )}
          <Route path="*" element={<Navigate to="/instructor" replace />} />
        </Routes>
      </div>
      <HelpWidget role="instructor" />
      <DataReplicationWidget />
    </>
  );
}